/**
 * Member Insights Translations Module (English)
 * 
 * Contains: Member Profile, Demographics, Motivations, Experience, Alumni Network
 * 
 * @module members
 * @language en
 */

export const members = {
  memberInsightsPage: {
    pageSubtitle: "Demographics, Motivations & Experience Survey Results from 3,790+ respondents"
  },
  membershipExperience: {
    title: "Member Experience Survey",
    subtitle: "Understanding member satisfaction and engagement across all touchpoints",
    overallSatisfaction: "Overall Satisfaction",
    programQuality: "Program Quality",
    networkingOpportunities: "Networking Opportunities",
    satisfactionMetrics: "Satisfaction Metrics",
    npsBreakdown: "Net Promoter Score Breakdown",
    benefitsValue: "Benefits Value",
    membershipJourney: "Membership Journey",
    general: "General Satisfaction",
    eventQuality: "Event Quality",
    networking: "Networking",
    personalDevelopment: "Personal Development",
    leadership: "Leadership",
    community: "Community Engagement",
    target: "Target",
    npsScore: "NPS Score",
    excellent: "Excellent",
    retentionRate: "Retention Rate",
    recommendation: "Would Recommend",
    promoters: "Promoters",
    neutrals: "Neutrals",
    detractors: "Detractors",
    professionalNetworking: "Professional Networking",
    leadershipDevelopment: "Leadership Development",
    personalGrowth: "Personal Growth",
    careerOpportunities: "Career Opportunities",
    communicationSkills: "Communication Skills",
    socialResponsibility: "Social Responsibility",
    businessKnowledge: "Business Knowledge",
    internationalConnections: "International Connections",
    high: "High",
    medium: "Medium",
    low: "Low",
    firstYear: "First Year",
    secondThirdYear: "2nd-3rd Year",
    fourthFifthYear: "4th-5th Year",
    fivePlusYears: "5+ Years",
    satisfaction: "Satisfaction",
    engagement: "Engagement",
    retention: "Retention"
  },
  occupation: {
    title: "Occupational and Industry Analysis",
    subtitle: "Detailed professional profile of JCI members",
    leadership: "Leadership",
    entrepreneurs: "Entrepreneurs",
    mainSectors: "Main Sectors",
    careerSatisfaction: "Career Satisfaction",
    goalOriented: "Goal Oriented",
    industryDistribution: "Industry Distribution",
    occupationalLevels: "Occupational Levels",
    hierarchicalLevels: "Hierarchical",
    entrepreneurialProfile: "Entrepreneurial Profile",
    careerStages: "Career Stages",
    professionalGoals: "Professional Goals",
    salaryRanges: "Salary Ranges",
    technology: "Technology",
    financialServices: "Financial Services",
    consulting: "Consulting",
    ecommerce: "E-commerce",
    health: "Health",
    education: "Education",
    marketing: "Marketing",
    manufacturing: "Manufacturing",
    realEstate: "Real Estate",
    others: "Others"
  },
  memberProfile: {
    title: "JCI Member Profile",
    surveyDescription: "2025 Membership Experience Survey • 3,790 respondents across all regions",
    averageAge: "Average Age",
    ageRange: "Range: 18-40",
    universityEducated: "University Educated",
    degreesHeldPursuing: "Degrees held or pursuing",
    entrepreneurs: "Entrepreneurs",
    selfEmployedMembers: "Self-employed members",
    surveyRespondents: "Survey Respondents",
    allJCIAreas: "All 4 JCI Areas",
    ageGroupAnalysis: "Age Group Analysis",
    coreAgeGroup: "Core 28-40 years",
    ofMembers: "of members",
    challenges: "Challenges",
    strengths: "Strengths",
    averageAgeByRegion: "Average Age by Region",
    youngest: "Youngest",
    oldest: "Oldest",
    membershipTenureAnalysis: "Membership Tenure Analysis",
    newMembers: "New Members",
    strongOnboarding: "Strong onboarding pipeline",
    buildingFoundation: "Building Foundation",
    developingSkills: "Developing skills & network",
    activeContributors: "Active Contributors",
    peakEngagement: "Peak engagement period",
    experiencedLeaders: "Experienced Leaders",
    mentorshipRoles: "Mentorship & leadership roles",
    coreLeaders: "Core Leaders",
    institutionalKnowledge: "Institutional knowledge",
    years: "years",
    freshEnergy: "Fresh energy",
    alumniNetwork: "Alumni Network",
    generationalContinuity: "Generational continuity through mentorship",
    alumniClubs: "Alumni Clubs",
    nations: "Nations",
    avgStay: "Avg. Stay",
    alumniTransition: "Age 40+ transition to Alumni",
    insight: "Insight",
    leadershipProgression: "Leadership Progression",
    localChapterLeaders: "Local Organization Leaders",
    foundationOfJCI: "Foundation of JCI",
    nationalDirectors: "National Directors",
    regionalLeadership: "Regional leadership",
    jciSenators: "JCI Senators",
    distinguishedAlumni: "Distinguished alumni",
    internationalOfficers: "International Officers",
    globalLeadership: "Global leadership cohort",
    majority: "Majority",
    significant: "Significant",
    growing: "Growing",
    educationalAttainment: "Educational Attainment",
    employmentDistribution: "Employment Distribution",
    bachelorsDegree: "Bachelor's Degree",
    mastersDegree: "Master's Degree",
    mastersEnrolled: "Master's (Enrolled)",
    phdPursuing: "PhD Pursuing",
    technicalVocational: "Technical/Vocational",
    fullTimeEmployed: "Full-time Employed",
    entrepreneurSelfEmployed: "Entrepreneur/Self-employed",
    student: "Student",
    other: "Other",
    universityDegrees: "97%+ of JCI members hold or are pursuing university degrees",
    entrepreneursInsight: "31% are entrepreneurs or self-employed",
    industryDiversity: "Industry Diversity",
    primarySectors: "Primary sectors represented by JCI members",
    education: "Education",
    technology: "Technology",
    finance: "Finance",
    healthcare: "Healthcare",
    nonprofit: "Nonprofit",
    government: "Government",
    manufacturing: "Manufacturing",
    services: "Services",
    careerEntry: "Career Entry",
    financialIndependence: "Financial Independence",
    leadershipPositions: "Leadership Positions",
    workLifeBalance: "Work-Life Balance",
    communityImpact: "Community Impact",
    ageGroupChallenges: "Age Group Challenges",
    ageGroupStrengths: "Age Group Strengths",
    familyResponsibilities: "Family responsibilities",
    careerGrowth: "Career growth demands",
    workPressure: "Work pressure",
    experience: "Experience",
    leadership: "Leadership",
    focus: "Focus",
    workLifeIntegration: "Work-life integration",
    leadershipDemands: "Leadership demands",
    mentorship: "Mentorship skills",
    strategicThinking: "Strategic thinking",
    networkBuilding: "Network building",
    educationBalance: "Education Balance",
    digitalNative: "Digital Native",
    freshPerspective: "Fresh Perspective",
    enthusiasm: "Enthusiasm",
    careerDevelopment: "Career Development",
    financialPressure: "Financial Pressure",
    networking: "Networking",
    ambition: "Ambition",
    innovation: "Innovation",
    timeForMentoring: "Time for Mentoring",
    industryExpertise: "Industry Expertise"
  },
  memberMotivations: {
    title: "Member Motivations & Values",
    subtitle: "What drives JCI member engagement",
    whyJoinJCI: "Why Members Join JCI",
    coreMotivations: "Core Motivations",
    personalGrowth: "Personal Growth",
    personalGrowthDesc: "Self-development and skill building",
    leadershipDevelopment: "Leadership Development",
    leadershipDesc: "Leadership skills and experience",
    networking: "Networking",
    networkingDesc: "Professional and social connections",
    communityImpact: "Community Impact",
    communityDesc: "Making a difference locally",
    careerOpportunities: "Career Opportunities",
    careerDesc: "Professional advancement",
    businessDevelopment: "Business Development",
    businessDesc: "Growing their business or career",
    internationalExposure: "International Exposure",
    internationalDesc: "Global connections and travel",
    socialBelonging: "Social Belonging",
    socialDesc: "Friendship and community",
    ofRespondents: "of respondents",
    topMotivation: "Top motivation",
    benefitsRanking: "Benefits Ranking",
    benefitsCitation: "Top benefits cited by members in order of importance",
    leadershipTraining: "Leadership Training",
    professionalNetwork: "Professional Network",
    personalDevelopment: "Personal Development",
    internationalConnections: "International Connections",
    communityService: "Community Service",
    businessConnections: "Business Connections",
    friendships: "Friendships",
    recognition: "Recognition",
    valueDrivenMembership: "Value-Driven Membership",
    valueDrivenDesc: "JCI members are primarily motivated by personal development (52%) and making impact (38%), with career benefits being secondary motivators.",
    riseProjects: "RISE Projects",
    peerMentorship: "Peer mentorship focus",
    digitalSkillsWorkshops: "Digital Skills Workshops",
    increasingImportance: "Increasing importance",
    keyInsightsTitle: "Key Insights from Member Experience Survey 2025",
    memberPriorities: "Member Priorities",
    personalGrowthTop: "Personal growth is the top motivator (28%)",
    leadershipDrives: "Leadership development drives engagement (24%)",
    strongEmphasis: "Strong emphasis on networking and connections (21%)",
    commitmentService: "Commitment to community service remains core (17%)",
    valueProposition: "Value Proposition",
    jciDelivers: "JCI delivers on leadership development expectations",
    trainingValued: "Training programs are highly valued (24%)",
    globalNetworkKey: "Global network access is a key differentiator",
    tangibleImpact: "Members seek tangible impact opportunities",
    sources: "Sources: Member Experience Survey 2025 (3,790 respondents) • JCI Impact Report 2025",
    topSkill: "Top Skills to Develop",
    topSkills: "Top Skills to Develop",
    rankedByInterest: "Ranked by member interest level",
    survey2025: "Source: JCI Member Experience Survey 2025",
    communityBuilding: "Community Building & Service",
    priority1: "Priority 1",
    publicSpeaking: "Public speaking & debate programs",
    criticalThinking: "Critical Thinking & Problem Solving",
    analysis: "Analysis and structured problem-solving",
    leadership: "Leadership & Team Building",
    teamBuilding: "Team leadership and collaboration skills",
    emotionalIntelligence: "Emotional Intelligence",
    selfAwareness: "Self-awareness and interpersonal skills",
    motivationAnalysis: "Motivation Analysis",
    understandingMotivations: "Understanding what drives JCI member engagement",
    memberExperienceSurvey: "Source: Member Experience Survey 2025",
    whyMembersJoin: "Why Members Join JCI",
    mostValuedBenefits: "Most Valued Benefits",
    accessToTraining: "Access to Training & Certifications",
    professionalWorkshops: "Professional workshops and courses",
    globalNetwork: "Global Network & Connections",
    internationalPartnerships: "International partnerships and networking",
    structuredPrograms: "Structured development programs",
    growthThrough: "Growth through experience and projects",
    topSkillsDevelopment: "Top Skills to Develop",
    communicationSkills: "Communication Skills",
    leadershipOpportunities: "Leadership Opportunities",
    accessToRoles: "Access to leadership roles and positions",
    professionalNetworking: "Professional Networking",
    buildingConnections: "Building professional connections",
    motivationsTitle: "Motivation Analysis",
    connectionsAcross: "Connections across regions and countries",
    impactOpportunities: "Impact Opportunities",
    meaningfulChange: "Opportunities to create meaningful change",
    leadershipTitle: "Leadership & Team Building",
    publicSpeakingCompetitions: "Public Speaking Competitions",
    debatingChampions: "Debating Champions",
    viewProgramsEvents: "View Programs & Events",
    priorityNumber: "#1 Priority",
    associatedPrograms: "Associated Programs",
    viewPrograms: "View Programs",
    positiveImpact: "Making positive impact in communities",
    communication: "Communication & Public Speaking",
    leadershipManagement: "Leadership & Management",
    technicalSkills: "Technical & Digital Skills",
    digitalLiteracy: "Digital literacy and tech skills",
    emerging: "Emerging",
    publicSpeakingCompetition: "Public Speaking Competition",
    participants2024: "52 participants (2024)",
    debatingChampionship: "Debating Championship",
    participants57: "57 teams (2024)",
    leadershipTrainingSessions: "Leadership Training Sessions",
    sessionsDelivered: "2,400+ sessions delivered",
    toypProgram: "TOYP Program",
    globalRecognition: "Global recognition program",
    leadershipWorkshops: "Leadership Workshops",
    globalEventParticipants: "Multiple global events",
    surveySource: "Based on Member Experience Survey 2025 with 3,790 respondents",
    skillsSource: "Skills data from Member Experience Survey 2025",
    motivationsAndBenefits: "Member Motivations & Benefits"
  },
  demographics: {
    title: "Member Demographics",
    subtitle: "Detailed breakdown of JCI member education, employment and age profile",
    averageMemberAge: "Average Member Age",
    activeMembers: "Active JCI members worldwide",
    yearsOld: "years old",
    educationProfile: "Education Profile",
    source: "Source",
    jciMemberSurvey: "2025 JCI Member Survey",
    universityEducated: "University Educated",
    highlyEducated: "JCI members are highly educated professionals",
    employmentStatus: "Employment Status",
    sourceCitation: "Source: 2025 JCI Member Survey - Official demographic data"
  },
  globalMemberProfile: {
    title: "Global Member Profile",
    surveyDescription: "2025 Membership Experience Survey • 3,790 respondents",
    educationalAttainment: "Educational Attainment",
    employmentDistribution: "Employment Distribution",
    industryDiversity: "Industry Diversity",
    keyInsight: "97%+ of JCI members hold or are pursuing university degrees",
    entrepreneursInsight: "31% are entrepreneurs or self-employed",
    primarySectors: "Primary sectors represented by JCI members",
    sourceCitation: "Source: JCI Membership Data 2025 | JCI Impact Report 2025 | 2025 Membership Experience Survey"
  },
  memberExperience: {
    title: "Member Experience Dashboard",
    subtitle: "Member satisfaction and engagement metrics",
    netPromoterScore: "Net Promoter Score",
    npsBreakdown: "NPS Breakdown",
    memberSatisfaction: "Member Satisfaction",
    promoters: "Promoters",
    neutrals: "Neutrals",
    detractors: "Detractors"
  },
  alumniNetwork: {
    title: "Alumni Network",
    subtitle: "JCI alumni global network - impact beyond active membership",
    totalAlumni: "Total Alumni",
    totalAlumniDesc: "Global network of JCI alumni",
    remainingActive: "Active Alumni",
    remainingActiveDesc: "Maintain some level of involvement post-JCI",
    leadershipPositions: "Leadership Positions",
    leadershipPositionsDesc: "In corporate, government, or nonprofit leadership",
    businessOwners: "Business Owners",
    businessOwnersDesc: "Started or inherited businesses"
  },
  eventPreferences: {
    title: "Event Preferences",
    subtitle: "Event types members value most",
    networking: "Networking Events",
    training: "Training Workshops",
    conferences: "Conferences",
    community: "Community Projects",
    social: "Social Events"
  },
  occupationIndustry: {
    title: "Occupation & Industry",
    subtitle: "Member occupational distribution and industry sectors",
    topOccupations: "Top Occupations",
    topIndustries: "Top Industries",
    entrepreneur: "Entrepreneur/Self-employed",
    manager: "Manager/Director",
    professional: "Professional",
    consultant: "Consultant",
    executive: "Executive",
    techIt: "Technology/IT",
    finance: "Finance/Banking",
    manufacturing: "Manufacturing/Industrial",
    healthcare: "Healthcare",
    consulting: "Consulting"
  },
  skillsDevelopment: {
    title: "Skills Development Dashboard",
    headline: "Training 1,969+ Leaders Annually",
    heroDescription: "Developing leaders is at the heart of JCI's mission. In 2024-2025, JCI delivered world-class training across four Areas of Opportunity, reaching nearly 2,000 members with practical skills in leadership, communication, entrepreneurship, and civic responsibility.",
    totalParticipants: "Total Participants Trained",
    members: "members",
    leadershipSessions: "Leadership Training Sessions",
    globally: "globally",
    skillsSessions: "Skills Development Sessions",
    totalSessions: "Total Sessions",
    structuredExperiences: "structured learning experiences",
    certifiedTrainers: "Certified Trainers Network",
    activeTrainers: "active trainers",
    certifiedTrainersByRegion: "Certified Trainers by Region (Multiplier Effect)",
    region: "Region",
    trainers: "Certified Trainers",
    percentGlobal: "% of Global",
    trainingMultiplier: "Training Multiplier",
    asiaPacific: "Asia and the Pacific",
    africaMiddleEast: "Africa and the Middle East",
    americas: "America",
    europe: "Europe",
    highestCapacity: "Highest capacity",
    rapidGrowth: "Rapid growth",
    establishedNetwork: "Established network",
    steadyPresence: "Steady presence",
    cascadingKnowledge: "Each trainer can certify 5-10 additional trainers",
    specializedTracks: "Specialized Training Tracks",
    publicSpeakingTrack: "Public Speaking & Communication",
    publicSpeakingCompetitors: "52 competitors in 2025",
    publicSpeakingImpact: "Winners advance to become trainers, mentors, and advocates",
    debatingTrack: "Debating & Critical Thinking",
    debatingTeams: "57 teams competing",
    debatingLanguages: "3 languages: English, French, Spanish",
    debatingImpact: "Builds analytical skills and cross-cultural dialogue",
    trainerCertification: "Trainer Certification Program",
    trainerTypes: "CNTs, CATs, Global Trainers, Training Mentors",
    trainerPathway: "Participant → Certified Trainer → Mentor",
    conferenceTraining: "Training Depth by Conference (2025)",
    conference: "Conference",
    location: "Location",
    participants: "Participants",
    sessions: "Sessions",
    regionalReach: "Regional Reach",
    asiaPacificConference: "JCI Asia & Pacific",
    ulaanbaatar: "Ulaanbaatar",
    africaConference: "JCI Africa & Middle East",
    durban: "Durban",
    americasConference: "JCI Conference of America",
    roatan: "Roatán",
    europeConference: "JCI European Conference",
    herning: "Herning",
    countriesCount: "countries",
    skillsCategories: "Skills Categories Covered",
    leadershipDecision: "Leadership & Decision-Making",
    communicationInfluence: "Communication & Influence",
    entrepreneurshipInnovation: "Entrepreneurship & Innovation",
    projectManagement: "Project Management & Execution",
    civicResponsibility: "Civic Responsibility & Global Citizenship",
    sessionCount: "sessions",
    memberProgression: "Member Progression Pathway",
    stage1: "Stage 1: Participant",
    stage1Desc: "Gain practical skills in leadership, communication, business",
    stage2: "Stage 2: Practitioner",
    stage2Desc: "Apply skills in projects, events, team leadership",
    stage3: "Stage 3: Certified Trainer",
    stage3Desc: "Deliver training to peers, mentor emerging leaders",
    stage4: "Stage 4: Master Trainer/Mentor",
    stage4Desc: "Design curriculum, mentor trainers, shape standards",
    membersCount: "1,969 members trained",
    returnToLocal: "Return to local organizations",
    selectedAnnually: "97 selected annually",
    experiencedTrainers: "Selected from experienced trainers",
    ctaTitle: "Invest in Your Leadership Journey",
    exploreTrainings: "Explore Available Trainings",
    becomeCertified: "Become a Certified Trainer",
    sourceNote: "Source: JCI Impact Report 2025 – Skills Development Data"
  },
  jciFoundation: {
    title: "JCI Foundation Grants Program",
    headline: "$100K+ Annually: Funding Member Innovation",
    heroDescription: "The JCI Foundation represents more than fundraising—it's a commitment to democratizing leadership opportunities and ensuring that great ideas aren't limited by budget constraints. Each year, the Foundation invests $100,000+ in grants that directly support member-led projects, leadership academies, entrepreneurship contests, and organizational infrastructure across all regions.",
    grantsOverTime: "Foundation Grants Over Time",
    year: "Year",
    totalAwarded: "Total Grants Awarded (USD)",
    trend: "Trend",
    interpretation: "Interpretation",
    highInvestment: "High investment year",
    consolidation: "Consolidation phase",
    focusOnImpact: "Focus on impact over volume",
    threeYearTotal: "3-Year Total: $405,000 invested in JCI member initiatives",
    whatGrantsFund: "What Grants Fund (2024-2025)",
    programsCompetitions: "JCI Programs Competitions",
    programsCompetitionsDesc: "CYE, TOYP, Debating & Public Speaking championship logistics",
    leadershipAcademies: "Leadership Academies & Summits",
    leadershipAcademiesDesc: "Regional training centers, curriculum development, facilitator certification",
    entrepreneurshipSupport: "Entrepreneurship & Business Support",
    entrepreneurshipSupportDesc: "JIB network infrastructure, startup mentorship, B2B matching",
    infrastructureSupport: "Physical Secretariat & Infrastructure",
    infrastructureSupportDesc: "National organization setup, digital tools, communication systems",
    growthInitiatives: "Growth & Development Initiatives",
    growthInitiativesDesc: "New Local Organization establishment, membership expansion",
    budgetImpact: "Budget Impact",
    reach: "Reach",
    trainingMembers: "Training 1,000+ members annually",
    entrepreneursEngaged: "345+ entrepreneurs engaged",
    orgsSupported: "114 national organizations supported",
    localOrgsSupported: "Supporting 4,641 local organizations",
    regionalAllocation: "Regional Allocation",
    typicalAllocation: "Typical Allocation",
    priorityAreas: "Priority Areas",
    leadershipCompetitions: "Leadership academies, competitions",
    infrastructureGrowth: "Infrastructure, growth initiatives",
    entrepreneurshipBusiness: "Entrepreneurship, business programs",
    trainingInnovation: "Training, innovation, partnerships",
    successStories: "Success Stories from Foundation Grants",
    story1Title: "Leadership Academy Expansion (South Africa)",
    story1Grant: "Grant: $8,500",
    story1Result: "Result: 200+ new members trained in 2025",
    story1ROI: "ROI: 23.5 members trained per $1 invested",
    story2Title: "Entrepreneurship Contest (Southeast Asia)",
    story2Grant: "Grant: $12,000",
    story2Result: "Result: 108 entrepreneurs competing, 3 funded startups",
    story2ROI: "ROI: 2.8x direct economic multiplier",
    story3Title: "National Secretariat Setup (East Africa)",
    story3Grant: "Grant: $7,200",
    story3Result: "Result: Professionalized operations for 4,500+ members",
    story3ROI: "ROI: Infrastructure sustainability for 5+ years",
    grantCycle: "Grant Cycle Process",
    submissionPeriod: "Submission Period",
    submissionTiming: "Q1 & Q3",
    reviewApproval: "Review & Approval",
    reviewTiming: "4-6 weeks",
    fundingDeployment: "Funding Deployment",
    deploymentTiming: "Following approval",
    impactReporting: "Impact Reporting",
    reportingTiming: "Quarterly + annual",
    ctaTitle: "Fund Your JCI Initiative",
    viewOpportunities: "View Grant Opportunities",
    checkQualify: "Check If You Qualify",
    submitProposal: "Submit a Proposal",
    sourceNote: "Source: JCI Foundation Annual Report 2025"
  },
  jciSenate: {
    title: "JCI Senate Recognition",
    headline: "84,375 Lifetime Leaders: The JCI Senate",
    heroDescription: "The JCI Senate is more than an honor—it's a lifelong membership in a global community of exceptional leaders. With 84,375 Senators, JCI maintains meaningful relationships with former members who continue to mentor, advise, and support the organization's mission long after their active membership.",
    totalSenators: "Total Active Senators",
    allTime: "All Time",
    newSenators: "Senators Honored in 2025",
    newSelections: "new selections",
    annualGrowth: "Annual Growth Rate",
    sustainableGrowth: "sustainable, quality-focused",
    lifetimeValue: "Lifetime Value",
    unlimitedMentorship: "Unlimited mentorship, networking, engagement",
    demographicsDashboard: "Senator Demographics",
    ageProfile: "Age Profile",
    ageRange: "Age Range",
    representation: "Representation",
    careerStage: "Career Stage",
    typicalRole: "Typical Role",
    earlyCareer: "Early career advancement",
    projectAdvisor: "Project advisor, peer mentor",
    midCareer: "Mid-career maturity",
    seniorMentor: "Senior mentor, strategic advisor",
    careerLeadership: "Career leadership",
    executiveAdvisor: "Executive advisor, board support",
    establishedExpertise: "Established expertise",
    elderStatesman: "Elder statesman/woman, legacy builder",
    averageAge: "Average Age: 33-40 years",
    primeYears: "Prime mentoring years: Peak influence window",
    genderComposition: "Gender Composition",
    male: "Male",
    female: "Female",
    professionalBackground: "Professional Background",
    corporateExecutives: "Corporate Executives",
    entrepreneursOwners: "Entrepreneurs/Business Owners",
    educatorsTrainers: "Educators & Trainers",
    governmentPublic: "Government & Public Sector",
    nonprofitSocial: "Non-Profit & Social Sector",
    otherFields: "Other Professional Fields",
    valueProposition: "Senate Value Proposition",
    benefit1Title: "Lifetime Recognition",
    benefit1Desc: "Permanent honor of exceptional service",
    benefit2Title: "Continued Community",
    benefit2Desc: "Network of 84K+ peer leaders globally",
    benefit3Title: "Mentorship Opportunity",
    benefit3Desc: "Guide emerging JCI leaders",
    benefit4Title: "Exclusive Access",
    benefit4Desc: "Senate events, thought leadership forums",
    benefit5Title: "Professional Network",
    benefit5Desc: "Connections across industries and countries",
    benefit6Title: "Legacy Impact",
    benefit6Desc: "Contribute to organizational strategy",
    engagementTiers: "Senate Engagement Model",
    tier1: "Tier 1: Mentorship",
    tier1Desc: "1-on-1 guidance for emerging leaders",
    tier1Active: "15,000-20,000 active mentors",
    tier1Impact: "30,000+ young leaders supported annually",
    tier2: "Tier 2: Program Leadership",
    tier2Desc: "Advisory roles for JCI programs",
    tier2Active: "5,000-7,000 active advisors",
    tier2Impact: "Judge competitions, validate standards",
    tier3: "Tier 3: Organizational Leadership",
    tier3Desc: "Board positions, committee roles, strategic planning",
    tier3Active: "500-1,000 active leaders",
    tier3Impact: "Govern at national/regional/global levels",
    tier4: "Tier 4: Legacy & Advocacy",
    tier4Desc: "Public ambassadors, thought leaders",
    tier4Active: "10,000+ active advocates",
    tier4Impact: "Promote leadership culture externally",
    successMetrics: "Senate Success Metrics",
    participationRate: "Active Participation Rate",
    participationValue: "35-40% of Senators",
    participationDesc: "Estimated 29,500-33,750 actively engaged",
    mentorshipNetwork: "Mentorship Network Size",
    mentorshipValue: "30,000+ mentee relationships",
    mentorshipDesc: "Ongoing guidance connections",
    contributionValue: "Lifetime Contribution Value",
    contributionAmount: "$500-2,000 per Senator",
    contributionDesc: "Mentoring, volunteering, advocacy",
    avgMembership: "Avg. Years Before Senatorship",
    avgMembershipValue: "12-15 years",
    avgMembershipDesc: "Active JCI membership",
    ctaTitle: "Join the JCI Senate",
    understandPath: "Understand Senatorship Path",
    exploreBenefits: "Explore Senator Benefits",
    becomeMentor: "Become a Mentor",
    nominateLeaders: "Nominate Outstanding Leaders",
    sourceNote: "Source: JCI Senate Registry 2025"
  }
} as const;

